import { CaseModel, CaseStatusEnum, CaseTypeEnum, TyreTypeEnum, ZipCodeArea } from 'api/model';
import useForm, { BasicFormState } from 'hooks/useForm';
import { toInputDateTimeString } from 'utils/date-helpers';
import { sweMobilePhoneNumberRegex } from 'utils/regex';
import { isVehicleLessCaseType } from '../CaseAssignments/utils';

export enum CaseDataField {
  caseTypeID,
  caseStatusID,
  closedDate,
  completedDate,
  companyID,
  registrationNumber,
  vehicleGearBox,
  vehicleMileage,
  customerName,
  customerPhoneNumber,
  bookerName,
  bookerPhoneNumber,
  dataFromRideManaged,
  rideOrderID,
  rideDeliveryID,
  InvoiceDataID,
  isElectric,
  isPremiumCar,
  isTrailer,
  isTruck,
  vehicleTyreType,
  vehicleYearModel,
  sendCustomerSurvey,
  isImportant,
  carTransportTruckID,
  supplier,
  originalFromName,
  originalFromAddress,
  originalFromCity,
  originalFromZip,
  originalFromCounty,
  originalToName,
  originalToAddress,
  originalToCity,
  originalToZip,
  originalToCounty,
  serviceIntervalKm,
  serviceIntervalMonths,
  timingBeltIntervalKm,
  timingBeltIntervalMonths,
  wLTPEnergyConsumptionKwhPer100Km,
  wLTPFuelConsumptionLitersPer100Km,
  energyCapacityKwh,
  vehicleFuel,
  VehicleHorsePower,
  vehicleBrand,
  vehicleValuation,
  vehicleModel,
  vehicleBody,
  VehicleWeight,
  sendCancellationSmsToBooker,
}

/**
 * Standard för "SMS till säljaren vid avbokning" när ärendet saknar eget val — speglar
 * backendens CaseTypeEnum.ShouldDefaultToSendCancellationSmsToBooker (Inköp, Hemleverans, Inbyte).
 */
export function caseTypeDefaultsToCancellationSmsToBooker(
  caseType: CaseTypeEnum | undefined
): boolean {
  return (
    [
      CaseTypeEnum.Purchase,
      CaseTypeEnum.HomeDelivery,
      CaseTypeEnum.TradeIn,
    ] as number[]
  ).includes(caseType ?? CaseTypeEnum.Purchase);
}

/** Gäller valet för ärendet: eget val om det finns, annars typens standard. Aldrig för typer utan fordon. */
export function effectiveSendCancellationSmsToBooker(
  caseType: CaseTypeEnum | undefined,
  storedValue: boolean | null | undefined
): boolean {
  if (caseType !== undefined && isVehicleLessCaseType(caseType)) {
    return false;
  }
  return storedValue ?? caseTypeDefaultsToCancellationSmsToBooker(caseType);
}

export function initializeCaseDataForm(
  caseModel?: CaseModel,
  caseType?: CaseTypeEnum
): BasicFormState<CaseDataField> {
  if (caseModel) {
    return {
      [CaseDataField.caseTypeID]: String(caseType ?? caseModel.caseTypeID),
      [CaseDataField.caseStatusID]: String(caseModel.caseStatusID),
      [CaseDataField.closedDate]: caseModel.closedDate
        ? toInputDateTimeString(caseModel.closedDate)
        : '',
      [CaseDataField.completedDate]: caseModel.completedDate
        ? toInputDateTimeString(caseModel.completedDate)
        : '',
      [CaseDataField.companyID]:
        caseModel.company?.companyID !== undefined &&
        caseModel.company?.companyID !== null
          ? String(caseModel.company.companyID)
          : caseModel.companyID !== undefined && caseModel.companyID !== null
            ? String(caseModel.companyID)
            : '',
      [CaseDataField.registrationNumber]: caseModel.registrationNumber,
      [CaseDataField.vehicleGearBox]: caseModel.vehicleGearBox
        ? 'true'
        : 'false',
      [CaseDataField.vehicleMileage]: String(caseModel.vehicleMileage),
      [CaseDataField.customerName]: caseModel.customerName,
      [CaseDataField.customerPhoneNumber]: caseModel.customerPhoneNumber,
      [CaseDataField.bookerName]: caseModel.bookerName,
      [CaseDataField.bookerPhoneNumber]: caseModel.bookerPhoneNumber,
      [CaseDataField.dataFromRideManaged]: caseModel.newDataFromRide
        ? 'false'
        : 'true',
      [CaseDataField.rideOrderID]: caseModel.rideOrderID
        ? String(caseModel.rideOrderID)
        : '',
      [CaseDataField.rideDeliveryID]: caseModel.rideDeliveryID
        ? String(caseModel.rideDeliveryID)
        : '',
      [CaseDataField.InvoiceDataID]: caseModel.invoiceDataID
        ? String(caseModel.invoiceDataID)
        : '',
      // Elbil is a mandatory tri-state answer: '' = not answered, 'true'/'false' = answered.
      [CaseDataField.isElectric]:
        caseModel.isElectric === true
          ? 'true'
          : caseModel.isElectric === false
            ? 'false'
            : '',
      [CaseDataField.isPremiumCar]: caseModel.isPremiumCar ? 'true' : 'false',
      [CaseDataField.isTrailer]: caseModel.isTrailer ? 'true' : 'false',
      [CaseDataField.isTruck]: caseModel.isTruck ? 'true' : 'false',
      [CaseDataField.vehicleTyreType]: caseModel.vehicleTyreType
        ? String(caseModel.vehicleTyreType)
        : '',
      [CaseDataField.vehicleYearModel]: caseModel.vehicleYearModel ?? '',
      [CaseDataField.sendCustomerSurvey]: caseModel.sendCustomerSurvey
        ? 'true'
        : 'false',
      [CaseDataField.isImportant]: caseModel.isImportant ? 'true' : 'false',
      [CaseDataField.carTransportTruckID]: caseModel.carTransportTruckID
        ? String(caseModel.carTransportTruckID)
        : '',
      [CaseDataField.supplier]: caseModel.supplierID
        ? String(caseModel.supplierID)
        : '',
      [CaseDataField.originalFromName]: caseModel.originalFromName ?? '',
      [CaseDataField.originalFromAddress]: caseModel.originalFromAddress ?? '',
      [CaseDataField.originalFromCity]: caseModel.originalFromCity ?? '',
      [CaseDataField.originalFromZip]: caseModel.originalFromZip ?? '',
      [CaseDataField.originalFromCounty]:
        caseModel.originalFromCounty?.id.toString() ?? '',
      [CaseDataField.originalToName]: caseModel.originalToName ?? '',
      [CaseDataField.originalToAddress]: caseModel.originalToAddress ?? '',
      [CaseDataField.originalToCity]: caseModel.originalToCity ?? '',
      [CaseDataField.originalToZip]: caseModel.originalToZip ?? '',
      [CaseDataField.originalToCounty]:
        caseModel.originalToCounty?.id.toString() ?? '',
      [CaseDataField.serviceIntervalKm]: caseModel.serviceIntervalKm
        ? String(caseModel.serviceIntervalKm)
        : '',
      [CaseDataField.serviceIntervalMonths]: caseModel.serviceIntervalMonths
        ? String(caseModel.serviceIntervalMonths)
        : '',
      [CaseDataField.timingBeltIntervalKm]: caseModel.timingBeltIntervalKm
        ? String(caseModel.timingBeltIntervalKm)
        : '',
      [CaseDataField.timingBeltIntervalMonths]: caseModel.timingBeltIntervalMonths
        ? String(caseModel.timingBeltIntervalMonths)
        : '',
      [CaseDataField.wLTPEnergyConsumptionKwhPer100Km]:
        caseModel.wltpEnergyConsumptionKwhPer100Km
          ? String(caseModel.wltpEnergyConsumptionKwhPer100Km)
          : '',
      [CaseDataField.wLTPFuelConsumptionLitersPer100Km]:
        caseModel.wltpFuelConsumptionLitersPer100Km
          ? String(caseModel.wltpFuelConsumptionLitersPer100Km)
          : '',
      [CaseDataField.energyCapacityKwh]: caseModel.energyCapacityKwh
        ? String(caseModel.energyCapacityKwh)
        : '',
      [CaseDataField.vehicleFuel]: caseModel.vehicleFuel ?? '',
      [CaseDataField.VehicleHorsePower]: caseModel.vehicleHorsePower
        ? String(caseModel.vehicleHorsePower)
        : '',
      [CaseDataField.vehicleBrand]: caseModel.vehicleBrand ?? '',
      [CaseDataField.vehicleValuation]: caseModel.vehicleValuation
        ? String(caseModel.vehicleValuation)
        : '',
      [CaseDataField.vehicleModel]: caseModel.vehicleModel ?? '',
      [CaseDataField.vehicleBody]: caseModel.vehicleBody ?? '',
      [CaseDataField.VehicleWeight]: caseModel.vehicleWeight
        ? String(caseModel.vehicleWeight)
        : '',
      [CaseDataField.sendCancellationSmsToBooker]:
        effectiveSendCancellationSmsToBooker(
          caseType ?? caseModel.caseTypeID,
          caseModel.sendCancellationSmsToBooker
        )
          ? 'true'
          : 'false',
    };
  }
  return {
    [CaseDataField.caseTypeID]: String(caseType ?? CaseTypeEnum.Purchase),
    [CaseDataField.caseStatusID]: String(CaseStatusEnum.Open),
    [CaseDataField.closedDate]: '',
    [CaseDataField.completedDate]: '',
    [CaseDataField.companyID]: '',
    [CaseDataField.registrationNumber]: '',
    [CaseDataField.vehicleGearBox]: 'true',
    [CaseDataField.vehicleMileage]: '0',
    [CaseDataField.customerName]: '',
    [CaseDataField.customerPhoneNumber]: '',
    [CaseDataField.bookerName]: '',
    [CaseDataField.bookerPhoneNumber]: '',
    [CaseDataField.dataFromRideManaged]: 'true',
    [CaseDataField.rideOrderID]: '',
    [CaseDataField.rideDeliveryID]: '',
    [CaseDataField.InvoiceDataID]: '',
    // Elbil has no default — the TL must explicitly answer Ja/Nej before the case can be saved.
    [CaseDataField.isElectric]: '',
    [CaseDataField.isPremiumCar]: 'false',
    [CaseDataField.isTrailer]: 'false',
    [CaseDataField.isTruck]: 'false',
    [CaseDataField.vehicleTyreType]: '',
    [CaseDataField.vehicleYearModel]: '',
    [CaseDataField.sendCustomerSurvey]: (
      [
      CaseTypeEnum.Purchase,
      CaseTypeEnum.HomeDelivery,
      CaseTypeEnum.Auction,
      ] as number[]
    ).includes(caseType ?? CaseTypeEnum.Purchase)
      ? 'true'
      : 'false',
    [CaseDataField.isImportant]: 'false',
    [CaseDataField.carTransportTruckID]: '',
    [CaseDataField.supplier]: '',
    [CaseDataField.originalFromName]: '',
    [CaseDataField.originalFromAddress]: '',
    [CaseDataField.originalFromCity]: '',
    [CaseDataField.originalFromZip]: '',
    [CaseDataField.originalFromCounty]: '',
    [CaseDataField.originalToName]: '',
    [CaseDataField.originalToAddress]: '',
    [CaseDataField.originalToCity]: '',
    [CaseDataField.originalToZip]: '',
    [CaseDataField.originalToCounty]: '',
    [CaseDataField.serviceIntervalKm]: '',
    [CaseDataField.serviceIntervalMonths]: '',
    [CaseDataField.timingBeltIntervalKm]: '',
    [CaseDataField.timingBeltIntervalMonths]: '',
    [CaseDataField.wLTPEnergyConsumptionKwhPer100Km]: '',
    [CaseDataField.wLTPFuelConsumptionLitersPer100Km]: '',
    [CaseDataField.energyCapacityKwh]: '',
    [CaseDataField.vehicleFuel]: '',
    [CaseDataField.VehicleHorsePower]: '',
    [CaseDataField.vehicleBrand]: '',
    [CaseDataField.vehicleValuation]: '',
    [CaseDataField.vehicleModel]: '',
    [CaseDataField.vehicleBody]: '',
    [CaseDataField.VehicleWeight]: '',
    [CaseDataField.sendCancellationSmsToBooker]:
      caseTypeDefaultsToCancellationSmsToBooker(caseType) ? 'true' : 'false',
  };
}

export function caseDataFormToCaseModel(
  base: CaseModel | undefined,
  form: BasicFormState<CaseDataField>,
  zipCodeAreadById: {
    [id: string]: ZipCodeArea;
  }
): CaseModel {
  const newCompanyId =
    form[CaseDataField.companyID] !== '' &&
    Number(form[CaseDataField.companyID]) > 0
      ? Number(form[CaseDataField.companyID])
      : undefined;

  let rideOrderID: number | undefined;
  let rideDeliveryID: number | undefined;
  if (
    form[CaseDataField.rideOrderID] !== '' &&
    form[CaseDataField.rideOrderID] !== undefined
  ) {
    rideOrderID = Number(form[CaseDataField.rideOrderID]);
  }
  if (
    form[CaseDataField.rideDeliveryID] !== '' &&
    form[CaseDataField.rideDeliveryID] !== undefined
  ) {
    rideDeliveryID = Number(form[CaseDataField.rideDeliveryID]);
  }

  return {
    adminHasUnhandledRequests: base?.adminHasUnhandledRequests ?? false,
    caseID: base?.caseID ?? -1,
    caseTypeID: Number(form[CaseDataField.caseTypeID]) as CaseTypeEnum,
    caseStatusID: Number(form[CaseDataField.caseStatusID]) as CaseStatusEnum,
    // För nya case sätter servern Created vid spar; tills dess används "nu" så
    // same-day-direktbok-jämförelsen kan köra mot bokningsdatum redan i UI:t.
    created: base?.created ?? new Date(),
    closedDate: form[CaseDataField.closedDate]
      ? new Date(form[CaseDataField.closedDate])
      : undefined,
    completedDate: form[CaseDataField.completedDate]
      ? new Date(form[CaseDataField.completedDate])
      : undefined,
    rideOrderID,
    rideDeliveryID,
    registrationNumber: form[CaseDataField.registrationNumber],
    vehicleGearBox:
      form[CaseDataField.vehicleGearBox] === 'true'
        ? true
        : form[CaseDataField.vehicleGearBox] === 'false'
          ? false
          : undefined,
    vehicleMileage: form[CaseDataField.vehicleMileage]
      ? Number(form[CaseDataField.vehicleMileage])
      : 0,
    bookerName: form[CaseDataField.bookerName],
    customerName: form[CaseDataField.customerName],
    customerPhoneNumber: form[CaseDataField.customerPhoneNumber],
    bookerPhoneNumber: form[CaseDataField.bookerPhoneNumber],
    timestamp: base?.timestamp ?? '',
    companyID:
      // Truthiness covers null (Number(null) would send companyID = 0),
      // undefined and 0 alike.
      newCompanyId ?? (base?.companyID ? Number(base.companyID) : undefined),
    assignmentList: [...(base?.assignmentList ?? [])],
    hasLinkedCases: base?.hasLinkedCases ?? false,
    adminHasUnreadComments: base?.adminHasUnreadComments ?? false,
    createdByID: base?.createdByID ?? -1,
    createdBy: base?.createdBy ?? '',
    modifiedByID: base?.modifiedByID ?? -1,
    modifiedBy: base?.modifiedBy ?? '',

    externalProtocolUrl: base?.externalProtocolUrl ?? '',
    externalTestUrl: base?.externalTestUrl ?? '',
    newDataFromRide: form[CaseDataField.dataFromRideManaged] === 'false',
    invoiceDataID: base?.invoiceDataID,
    // Bevara TL:ens direktbok-svar mellan form-edits — annars triggar
    // verifyDirectBookingAndProceed prompten igen efter ett spar för att
    // newCaseModel.isDirectBooking är undefined trots att DB:n har svaret.
    isDirectBooking: base?.isDirectBooking,
    // Tri-state: '' (not answered) maps to undefined so the server keeps it null and the
    // frontend validator can require an explicit Ja/Nej before save.
    isElectric:
      form[CaseDataField.isElectric] === 'true'
        ? true
        : form[CaseDataField.isElectric] === 'false'
          ? false
          : undefined,
    isPremiumCar: form[CaseDataField.isPremiumCar] === 'true',
    isTrailer: form[CaseDataField.isTrailer] === 'true',
    isTruck: form[CaseDataField.isTruck] === 'true',
    vehicleTyreType: form[CaseDataField.vehicleTyreType]
      ? (Number(form[CaseDataField.vehicleTyreType]) as TyreTypeEnum)
      : undefined,
    vehicleYearModel: form[CaseDataField.vehicleYearModel] ?? '',
    sendCustomerSurvey: form[CaseDataField.sendCustomerSurvey] === 'true',
    sendCancellationSmsToBooker:
      form[CaseDataField.sendCancellationSmsToBooker] === 'true',
    isImportant: form[CaseDataField.isImportant] === 'true',
    carTransportTruckID: form[CaseDataField.carTransportTruckID]
      ? Number(form[CaseDataField.carTransportTruckID])
      : undefined,
    supplierID: form[CaseDataField.supplier]
      ? Number(form[CaseDataField.supplier])
      : undefined,

    originalFromName: form[CaseDataField.originalFromName] ?? '',
    originalFromAddress: form[CaseDataField.originalFromAddress] ?? '',
    originalFromCity: form[CaseDataField.originalFromCity] ?? '',
    originalFromZip: form[CaseDataField.originalFromZip] ?? '',
    originalFromCounty:
      form[CaseDataField.originalFromCounty] &&
      form[CaseDataField.originalFromCounty] !== ''
        ? (zipCodeAreadById[form[CaseDataField.originalFromCounty]] ??
          undefined)
        : undefined,
    originalToName: form[CaseDataField.originalToName] ?? '',
    originalToAddress: form[CaseDataField.originalToAddress] ?? '',
    originalToCity: form[CaseDataField.originalToCity] ?? '',
    originalToZip: form[CaseDataField.originalToZip] ?? '',
    originalToCounty:
      form[CaseDataField.originalToCounty] &&
      form[CaseDataField.originalToCounty] !== ''
        ? (zipCodeAreadById[form[CaseDataField.originalToCounty]] ?? undefined)
        : undefined,
    serviceIntervalKm: form[CaseDataField.serviceIntervalKm]
      ? Number(form[CaseDataField.serviceIntervalKm])
      : undefined,
    serviceIntervalMonths: form[CaseDataField.serviceIntervalMonths]
      ? Number(form[CaseDataField.serviceIntervalMonths])
      : undefined,
    timingBeltIntervalKm: form[CaseDataField.timingBeltIntervalKm]
      ? Number(form[CaseDataField.timingBeltIntervalKm])
      : undefined,
    timingBeltIntervalMonths: form[CaseDataField.timingBeltIntervalMonths]
      ? Number(form[CaseDataField.timingBeltIntervalMonths])
      : undefined,
    wltpEnergyConsumptionKwhPer100Km: form[
      CaseDataField.wLTPEnergyConsumptionKwhPer100Km
    ]
      ? Number(form[CaseDataField.wLTPEnergyConsumptionKwhPer100Km])
      : undefined,
    wltpFuelConsumptionLitersPer100Km: form[
      CaseDataField.wLTPFuelConsumptionLitersPer100Km
    ]
      ? Number(form[CaseDataField.wLTPFuelConsumptionLitersPer100Km])
      : undefined,
    energyCapacityKwh: form[CaseDataField.energyCapacityKwh]
      ? Number(form[CaseDataField.energyCapacityKwh])
      : undefined,
    vehicleFuel: form[CaseDataField.vehicleFuel] ?? '',
    vehicleHorsePower: form[CaseDataField.VehicleHorsePower]
      ? Number(form[CaseDataField.VehicleHorsePower])
      : undefined,
    vehicleBrand: form[CaseDataField.vehicleBrand] ?? '',
    vehicleValuation: form[CaseDataField.vehicleValuation]
      ? Number(form[CaseDataField.vehicleValuation])
      : undefined,
    vehicleModel: form[CaseDataField.vehicleModel] ?? '',
    vehicleBody: form[CaseDataField.vehicleBody] ?? '',
    vehicleWeight: form[CaseDataField.VehicleWeight]
      ? Number(form[CaseDataField.VehicleWeight])
      : undefined,
  } satisfies CaseModel;
}

/**
 * Normaliserar ett registreringsnummer för jämförelse: tar bort blanksteg och
 * gör om till versaler så att "abc 123" och "ABC123" räknas som samma bil.
 */
export function normalizeRegistrationNumber(reg: string | undefined): string {
  return (reg ?? '').replace(/\s+/g, '').toUpperCase();
}

/**
 * Bilspecifika fält som ska nollställas när man kopierar ett uppdrag och byter
 * regnr — kopian ska köras med en annan bil, så all fordonsdata/flaggor som hör
 * till originalbilen måste bort (annars följer t.ex. märke, drivmedel, WLTP och
 * el-/släp-flaggor felaktigt med). Regnr och icke-bilspecifika fält (kund,
 * bokare, adresser m.m.) lämnas orörda så att förifyllningen fortsatt är till nytta.
 *
 * De härledda flaggorna (IsSupercar, IsHighMileage osv.) räknas om på servern i
 * ApplyClassification utifrån dessa fält, så det räcker att nollställa källfälten här.
 */
export function getClearedVehicleDataFormValues(): Partial<
  BasicFormState<CaseDataField>
> {
  return {
    [CaseDataField.vehicleGearBox]: 'true',
    [CaseDataField.vehicleMileage]: '0',
    [CaseDataField.vehicleYearModel]: '',
    [CaseDataField.vehicleBrand]: '',
    [CaseDataField.vehicleModel]: '',
    [CaseDataField.vehicleBody]: '',
    [CaseDataField.vehicleFuel]: '',
    [CaseDataField.vehicleValuation]: '',
    [CaseDataField.VehicleHorsePower]: '',
    [CaseDataField.VehicleWeight]: '',
    [CaseDataField.vehicleTyreType]: '',
    [CaseDataField.isElectric]: 'false',
    [CaseDataField.isPremiumCar]: 'false',
    [CaseDataField.isTrailer]: 'false',
    [CaseDataField.isTruck]: 'false',
    [CaseDataField.wLTPEnergyConsumptionKwhPer100Km]: '',
    [CaseDataField.wLTPFuelConsumptionLitersPer100Km]: '',
    [CaseDataField.energyCapacityKwh]: '',
    [CaseDataField.serviceIntervalKm]: '',
    [CaseDataField.serviceIntervalMonths]: '',
    [CaseDataField.timingBeltIntervalKm]: '',
    [CaseDataField.timingBeltIntervalMonths]: '',
  };
}

// Platshållare och lastbilsärenden validerar inte fordons- och kundfälten, och typer utan fordon
// (Uthyrd personal, Samåkning, Däckkörning) har dem dolda och frivilliga.
const isVehicleAndCustomerOptional = (caseTypeID: string) => {
  const caseType = Number(caseTypeID) as CaseTypeEnum;
  return (
    caseType === CaseTypeEnum.Placeholder ||
    caseType === CaseTypeEnum.CarTransportTruck ||
    isVehicleLessCaseType(caseType)
  );
};

const bookerOptionalCaseTypes = [
  CaseTypeEnum.RentedStaff,
  CaseTypeEnum.Samakning,
  CaseTypeEnum.Dackkorning,
  CaseTypeEnum.InternalDelivery,
  CaseTypeEnum.FaciliyExchange,
] as const;

const useCaseDataForm = (caseModel?: CaseModel) => {
  return useForm(initializeCaseDataForm(caseModel), {
    [CaseDataField.companyID]: (v, inputName, formState) =>
      ([CaseTypeEnum.Placeholder] as number[]).includes(
        Number(formState[CaseDataField.caseTypeID])
      ) || v
        ? undefined
        : { error: 'Ange ett företag' },
    [CaseDataField.registrationNumber]: (v, inputName, formState) =>
      !isVehicleAndCustomerOptional(formState[CaseDataField.caseTypeID]) &&
      v.trim().length === 0
        ? { error: 'Ange ett reg.nr' }
        : undefined,
    // Typer utan fordon är inte kundvända -> kund är frivillig (för Samåkning är fältet dolt).
    [CaseDataField.customerName]: (v, inputName, formState) =>
      !isVehicleAndCustomerOptional(formState[CaseDataField.caseTypeID]) &&
      v.trim().length === 0
        ? { error: 'Ange en kund' }
        : undefined,
    [CaseDataField.customerPhoneNumber]: (v, inputName, formState) =>
      ([
        CaseTypeEnum.Placeholder,
        CaseTypeEnum.CarTransportTruck,
        // Samåkning har inget Kund-tel-fält (dolt) -> ingen varning.
        CaseTypeEnum.Samakning,
      ] as number[]).includes(Number(formState[CaseDataField.caseTypeID])) ||
      sweMobilePhoneNumberRegex.test(v.replaceAll(' ', '').replaceAll('-', ''))
        ? undefined
        : { warning: 'Kontrollera att kundens telefonnummer är korrekt' },
    [CaseDataField.bookerName]: (v, inputName, formState) =>
      !([CaseTypeEnum.Placeholder, CaseTypeEnum.CarTransportTruck] as number[]).includes(
        Number(formState[CaseDataField.caseTypeID])
      ) &&
      !([...bookerOptionalCaseTypes] as number[]).includes(
        Number(formState[CaseDataField.caseTypeID])
      ) &&
      v.trim().length === 0
        ? { error: 'Ange en bokare' }
        : undefined,
    [CaseDataField.vehicleMileage]: (v, inputName, formState) =>
      !isVehicleAndCustomerOptional(formState[CaseDataField.caseTypeID]) &&
      Number(v) === 0
        ? { warning: 'Miltal är 0, är detta korrekt?' }
        : undefined,
    [CaseDataField.rideOrderID]: (v, inputName, formState) =>
      Number(formState[CaseDataField.caseTypeID]) !==
        CaseTypeEnum.Placeholder && Number.isNaN(Number(v))
        ? { warning: 'RideOrderID, Ange ett nummer eller lämna tomt' }
        : undefined,
    [CaseDataField.InvoiceDataID]: (v, inputName, formState) =>
      Number(formState[CaseDataField.caseTypeID]) !==
        CaseTypeEnum.Placeholder && Number.isNaN(Number(v))
        ? { warning: 'InvoiceDataID, Ange ett nummer eller lämna tomt' }
        : undefined,
    [CaseDataField.carTransportTruckID]: (v, inputName, formState) => {
      return Number(formState[CaseDataField.caseTypeID]) ===
        CaseTypeEnum.CarTransportTruck &&
        (v === '' || Number.isNaN(Number(v)))
        ? { error: 'Välj lastbil' }
        : undefined;
    },
    [CaseDataField.supplier]: (v, inputName, formState) =>
      Number(formState[CaseDataField.caseTypeID]) !==
        CaseTypeEnum.Placeholder && Number.isNaN(Number(v))
        ? { warning: 'Leverantör, Ange en leverantör' }
        : undefined,
    // Elbil måste besvaras Ja/Nej innan ärendet kan sparas (för fordonsärenden).
    [CaseDataField.isElectric]: (v, inputName, formState) =>
      !isVehicleAndCustomerOptional(formState[CaseDataField.caseTypeID]) &&
      v !== 'true' &&
      v !== 'false'
        ? { error: 'Ange om ärendet avser elbil (Ja/Nej)' }
        : undefined,
  });
};

export default useCaseDataForm;
